import statesData from "./indiaStates.json";
import districtsData from "./indiaDistricts.json";

const STATE_ALIASES = {
	keralam: "Kerala",
	orissa: "Odisha",
	pondicherry: "Puducherry",
	uttaranchal: "Uttarakhand",
	"tamilnadu": "Tamil Nadu",
	"tamil nadu": "Tamil Nadu",
};

const sortedStates = [...(statesData.states || [])].sort((a, b) =>
	a.stateName.localeCompare(b.stateName)
);

const stateByLowerName = new Map(
	sortedStates.map((s) => [s.stateName.toLowerCase(), s])
);

/** Flatten districts list in case of nested/corrupt JSON entries. */
const allDistricts = (districtsData.districts || []).flatMap((entry) =>
	Array.isArray(entry) ? entry.filter((d) => d && d.districtName) : entry ? [entry] : []
);

const districtsByStateCode = allDistricts.reduce((acc, d) => {
	if (!d?.stateCode || !d?.districtName) return acc;
	if (!acc[d.stateCode]) acc[d.stateCode] = [];
	acc[d.stateCode].push(d.districtName);
	return acc;
}, {});

Object.keys(districtsByStateCode).forEach((code) => {
	districtsByStateCode[code] = [...new Set(districtsByStateCode[code])].sort((a, b) =>
		a.localeCompare(b)
	);
});

/** Map lowercased district name → list of official state names that contain it. */
const statesByDistrictLower = allDistricts.reduce((acc, d) => {
	if (!d?.districtName || !d?.stateCode) return acc;
	const state = sortedStates.find((s) => s.stateCode === d.stateCode);
	if (!state) return acc;
	const key = d.districtName.toLowerCase();
	if (!acc[key]) acc[key] = [];
	if (!acc[key].includes(state.stateName)) acc[key].push(state.stateName);
	return acc;
}, {});

/** All Indian states and union territories (official names, sorted). */
export function getIndianStates() {
	return sortedStates;
}

/** Resolve a stored state string to the official state name (case/alias tolerant). */
export function resolveStateName(rawState) {
	if (rawState == null) return "";
	const trimmed = String(rawState).trim();
	if (!trimmed) return "";

	const exact = stateByLowerName.get(trimmed.toLowerCase());
	if (exact) return exact.stateName;

	const alias = STATE_ALIASES[trimmed.toLowerCase()];
	if (alias) return alias;

	return trimmed;
}

/** Infer state when only a district is known (unique district names only). */
export function inferStateFromDistrict(rawDistrict) {
	if (rawDistrict == null) return "";
	const key = String(rawDistrict).trim().toLowerCase();
	if (!key) return "";
	const matches = statesByDistrictLower[key] || [];
	return matches.length === 1 ? matches[0] : "";
}

/** Resolve district to the official spelling for a state, if possible. */
export function resolveDistrictName(stateName, rawDistrict) {
	if (rawDistrict == null) return "";
	const trimmed = String(rawDistrict).trim();
	if (!trimmed) return "";

	const list = getDistrictsForStateName(stateName);
	const match = list.find((d) => d.toLowerCase() === trimmed.toLowerCase());
	return match || trimmed;
}

/**
 * Normalize state + district for edit forms.
 * Backfills missing state from a unique district (common for legacy Kerala rows).
 */
export function normalizeSchoolLocation(rawState, rawDistrict) {
	let state = resolveStateName(rawState);
	const districtRaw =
		rawDistrict == null ? "" : String(rawDistrict).trim();

	if (!state && districtRaw) {
		state = inferStateFromDistrict(districtRaw);
	}

	const district = resolveDistrictName(state, districtRaw);
	return { state, district };
}

/** District display names for the given state name, sorted. */
export function getDistrictsForStateName(stateName) {
	const resolved = resolveStateName(stateName);
	if (!resolved) return [];
	const st = stateByLowerName.get(resolved.toLowerCase());
	if (!st) return [];
	return districtsByStateCode[st.stateCode] || [];
}
